import { SendEventDtoType } from "@cede/types";

/**
 * The browser caps the combined body of all in-flight `keepalive` requests at 64 KB
 * and rejects the fetch outright once over it — so an unload flush that ignores the
 * cap loses the whole batch rather than part of it. Budgeted below the limit to
 * leave room for other keepalive traffic on the page (Sentry's own transport).
 */
export const KEEPALIVE_BUDGET_BYTES = 48_000;

const encoder = new TextEncoder();

/** Bytes of the `{"events":[]}` envelope the request body always carries. */
const ENVELOPE_BYTES = encoder.encode(JSON.stringify({ events: [] })).length;

/**
 * Size of the request body these events produce, measured the same way the budget is,
 * so a reported failure can be read against `KEEPALIVE_BUDGET_BYTES` directly.
 */
export function serializedBytes(events: SendEventDtoType[]): number {
  return encoder.encode(JSON.stringify({ events })).length;
}

/**
 * Indices of the events whose serialized payload fits the keepalive budget.
 *
 * Oldest-first: the queue is chronological, and a partial flush should keep the
 * events the user has already moved past rather than the most recent ones. A single
 * event over the budget is skipped rather than ending the scan — otherwise one
 * oversized event blocks every smaller one behind it for the rest of the session.
 *
 * Indices, not events, so the caller can pair the sent payload back to the queued
 * originals it must acknowledge.
 */
export function takeUnloadBatch(
  events: SendEventDtoType[],
  budgetBytes: number = KEEPALIVE_BUDGET_BYTES,
): { indices: number[]; deferredCount: number } {
  const indices: number[] = [];
  let usedBytes = ENVELOPE_BYTES;

  for (let index = 0; index < events.length; index++) {
    // +1 for the comma this event adds once it is not the first in the array.
    const eventBytes = encoder.encode(JSON.stringify(events[index])).length + (indices.length > 0 ? 1 : 0);
    if (usedBytes + eventBytes > budgetBytes) continue;
    usedBytes += eventBytes;
    indices.push(index);
  }

  return { indices, deferredCount: events.length - indices.length };
}
